// ═══════════════════════════════════════════════════════════════════════
// os-store.ts — Agentic Operating System-wide UI state (Phase 1 scaffolding)
// ═══════════════════════════════════════════════════════════════════════
// Zustand store that holds Agentic Operating System-shell-level UI state shared across deeply-
// nested renderers. The first slice is the DetailDrawer (Commit 1a);
// later commits expand this with edit mode, pending writes, validation,
// and cross-screen selections. Components subscribe via selectors so
// unrelated state changes don't re-render the world.
//
// Why a store instead of prop drilling: row/card click handlers live 4
// levels deep in ComponentRenderer / KanbanRenderer. Threading a callback
// through every component just to toggle one drawer is the kind of
// boilerplate that compounds across Phase 1's surface area.
// ═══════════════════════════════════════════════════════════════════════

import { create } from "zustand";
import type { ScreenTemplate } from "@sloe/schema";
import { trackEvent } from "./telemetry";

interface DetailDrawerState {
  open: boolean;
  row: Record<string, any> | null;
  screen: ScreenTemplate | null;
}

/**
 * Mirror of ScreenContent's fetched + flattened rows, written through to
 * the store on every successful fetch. Read-side for editors that need
 * cross-row context (e.g. FieldSelectEditor falling back to distinct
 * values when ScreenField.options is empty). Single writer (ScreenContent),
 * many readers — keeps the lifecycle predictable.
 */
interface ScreenRowsCache {
  osId: string | null;
  screenId: string | null;
  rows: Record<string, any>[];
}

interface OsStore {
  drawer: DetailDrawerState;
  screenRows: ScreenRowsCache;
  screenRefreshKey: number;
  /**
   * Open the DetailDrawer for a given row on a given screen. Fires the
   * "screen_row_opened" telemetry event fire-and-forget — never blocks
   * UI. osId is passed per-call so the store stays stateless across Agentic Operating System
   * switches in the workspace switcher.
   */
  openDrawer: (
    row: Record<string, any>,
    screen: ScreenTemplate,
    osId: string,
  ) => void;
  closeDrawer: () => void;
  /** ScreenContent writes here after each successful fetch (or clears on Agentic Operating System/screen change). */
  setScreenRows: (osId: string | null, screenId: string | null, rows: Record<string, any>[]) => void;
  /**
   * Patch a single key on the currently-open drawer row. Used by editors
   * for optimistic UI updates and rollback (same call shape both ways).
   * No-op if the drawer is closed.
   */
  applyOptimisticUpdate: (key: string, value: any) => void;
  /** Editors call this after a successful PATCH to make ScreenContent refetch. */
  bumpScreenRefresh: () => void;
}

const CLOSED: DetailDrawerState = { open: false, row: null, screen: null };
const EMPTY_ROWS: ScreenRowsCache = { osId: null, screenId: null, rows: [] };

export const useOsStore = create<OsStore>((set) => ({
  drawer: CLOSED,
  screenRows: EMPTY_ROWS,
  screenRefreshKey: 0,
  openDrawer: (row, screen, osId) => {
    set({ drawer: { open: true, row, screen } });
    void trackEvent(osId, {
      event_type: "screen_row_opened",
      screen_id: screen.screen_id,
      metadata: { row_id: row?.id ?? null },
    });
  },
  closeDrawer: () => set({ drawer: CLOSED }),
  setScreenRows: (osId, screenId, rows) => set({ screenRows: { osId, screenId, rows } }),
  applyOptimisticUpdate: (key, value) => set((state) => {
    if (!state.drawer.row) return {};
    return {
      drawer: {
        ...state.drawer,
        row: { ...state.drawer.row, [key]: value },
      },
    };
  }),
  bumpScreenRefresh: () => set((state) => ({ screenRefreshKey: state.screenRefreshKey + 1 })),
}));
